import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import request from '../helpers/request';

export const getPolicies = createAsyncThunk(
    'policies/getPolicies',
    async (_, thunkAPI) => {
        try {
            const response = await request.get(`/api/v1/compliance/policy/`);
            return response.data;
        } catch (e) {
            console.error("PoliciesReducer: ", e)
            return thunkAPI.rejectWithValue('Failed to fetch policies')
        }
    }
);

export const getTasks = createAsyncThunk(
  'tasks/getTasks',
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const {
        page,
        page_size,
        ordering,
        isDescending,
        search,
        filters,
      } = state.policies.pagination;

      const params = {
        ...(page && { page }),
        ...(page_size && { page_size }),
        ...(ordering && { ordering: isDescending ? ordering : `-${ordering}` }),
        ...(search && { search }),
        ...(filters && typeof filters === 'object' && Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== null && v !== '')
        )),
      };

      const response = await request.get('/api/v1/tools/task/', { params });

      return response.data;
    } catch (e) {
      console.error('TasksReducer: Failed to fetch tasks -', e.message || e);
      return thunkAPI.rejectWithValue(e.message || 'Failed to fetch tasks');
    }
  }
);


export const getWaitingPages = createAsyncThunk(
    'policies/getWaitingPages',
    async (_, thunkAPI) => {
        try {
            const response = await request.get(`/api/v1/compliance/page/`);
            return response.data;
        } catch (e) {
            console.error("PoliciesReducer: ", e)
            return thunkAPI.rejectWithValue('Failed to fetch policies')
        }
    }
);

export const getSubscribers = createAsyncThunk(
    'subscribers/getSubscribers',
    async (_, thunkAPI) => {
        try {
            const response = await request.get(`/api/v1/tools/subscribers/`);
            return response.data;
        } catch (e) {
            console.error("SubscribersReducer: ", e);
            return thunkAPI.rejectWithValue('Failed to fetch subscribers');
        }
    }
);

export const getCompletedTasksToday = createAsyncThunk(
    'tasks/getCompletedTasksToday',
    async (_, thunkAPI) => {
        try {
            const response = await request.get('/api/v1/tools/task/completed-today/');
            return response.data.count;
        } catch (e) {
            console.error("TasksReducer: Failed to fetch completed tasks today -", e.message || e);
            return thunkAPI.rejectWithValue(e.message || 'Failed to fetch completed tasks today');
        }
    }
);

const initialState = {
    policies: [],
    isPoliciesLoading: false,
    tasks: [],
    isTasksLoading: true,
    hasFetchedTasks: false,
    pages: [],
    pagination: {
        count: 0,
        page: 1,
        page_size: 100,
        ordering: null,
        search: null,
        isDescending: false,
        filters: {},
    },
    subscribers: [],
    isSubscribersLoading: false,
    completedTasksToday: 0,
    isCompletedTasksLoading: false,
};

const policiesSlice = createSlice({
    name: 'policies',
    initialState,
    reducers: {
        setPagination: (state, action) => {
            const { page, page_size, ordering, isDescending } = action.payload;

            if (page_size && page_size !== state.pagination.page_size) {
                state.pagination.page = 1;
            } else if (page) {
                state.pagination.page = page;
            }

            if (page_size) {
                state.pagination.page_size = page_size;
            }

            // Handle ordering (including clearing sort)
            if (ordering === null) {
                state.pagination.ordering = null;
                state.pagination.isDescending = false;
            } else if (ordering) {
                state.pagination.isDescending =
                    ordering === state.pagination.ordering
                        ? !state.pagination.isDescending
                        : true;
                state.pagination.ordering = ordering;
            }

            // Optional: also handle explicit isDescending override
            if (typeof isDescending === 'boolean') {
                state.pagination.isDescending = isDescending;
            }
        },
        setSearch: (state, action) => {
            const search = action.payload;
            state.pagination.search = search;
        },
        setFilters: (state, action) => {
        state.pagination.filters = {
            ...state.pagination.filters,
            ...action.payload,
        };
    },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getPolicies.pending, (state) => {
                state.isPoliciesLoading = true;
            })
            .addCase(getPolicies.fulfilled, (state, action) => {
                state.policies = action.payload;
                state.isPoliciesLoading = false;
            })
            .addCase(getPolicies.rejected, (state) => {
                state.isPoliciesLoading = false;
            })
            .addCase(getTasks.pending, (state) => {
                state.isTasksLoading = true;
            })
            .addCase(getTasks.fulfilled, (state, action) => {
                state.tasks = action.payload.results
                state.pagination.count = action.payload.count;
                state.isTasksLoading = false;
                state.hasFetchedTasks = true;
            })
            .addCase(getTasks.rejected, (state) => {
                state.isTasksLoading = false;
                state.hasFetchedTasks = true;
            })
            .addCase(getWaitingPages.pending, (state) => {
                state.isTasksLoading = true; 
            })
            .addCase(getWaitingPages.fulfilled, (state, action) => {
                state.pages = action.payload;
                state.isTasksLoading = false; 
            })
            .addCase(getWaitingPages.rejected, (state) => {
                state.isTasksLoading = false; 
            })
            .addCase(getSubscribers.pending, (state) => {
                state.isSubscribersLoading = true;
            })
            .addCase(getSubscribers.fulfilled, (state, action) => {
                state.subscribers = action.payload;
                state.isSubscribersLoading = false;
            })
            .addCase(getSubscribers.rejected, (state) => {
                state.isSubscribersLoading = false;
            })
            .addCase(getCompletedTasksToday.pending, (state) => {
                state.isCompletedTasksLoading = true;
            })
            .addCase(getCompletedTasksToday.fulfilled, (state, action) => {
                state.completedTasksToday = action.payload;
                state.isCompletedTasksLoading = false;
            })
            .addCase(getCompletedTasksToday.rejected, (state) => {
                state.isCompletedTasksLoading = false;
            });
    },
});

export const { setPagination, setSearch, setFilters } = policiesSlice.actions;
export default policiesSlice.reducer;